import { IconCode } from "@/assets/icons/geist/IconCode";
import { Button } from "@/components/flexkit/Button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/flexkit/Collapsible";
import { Tooltip } from "@/components/Tooltip";
import { cn } from "@/components/podkit/lib/cn";
import { Text } from "@/components/podkit/typography/Text";
import type { AgentMessageFragmentFileModification } from "@/hooks/use-stream-conversation";
import { useTemporaryState } from "@/hooks/use-temporary-value";
import { toast } from "@/components/flexkit/toast/use-toast";
import { formatError } from "@/utils/errors";
import { useTheme } from "@/hooks/use-theme";
import "@/routes/environments/code-changes/themes/register";
import { convertDiffGroupsToPatch, parseDiffContent, type ParsedDiff } from "@/components/ai/fragment-diff-utils";
import { ScrollArea } from "@/components/flexkit/ScrollArea";
import { PatchDiff } from "@pierre/diffs/react";
import { Check, Copy } from "lucide-react";
import { useCallback, useMemo, type FC } from "react";

export const FragmentFileModification: FC<{
    fragment: AgentMessageFragmentFileModification;
    defaultOpen?: boolean;
    onHandleReveal?: () => void;
}> = ({ fragment, defaultOpen = true, onHandleReveal }) => {
    const diff = useMemo((): ParsedDiff => {
        if (fragment.diff) {
            return convertDiffGroupsToPatch(
                fragment.filePath,
                fragment.startLine,
                fragment.diff.diffGroups,
                fragment.diff.stats,
            );
        }
        return parseDiffContent(fragment.content, fragment.filePath, fragment.startLine);
    }, [fragment.content, fragment.diff, fragment.filePath, fragment.startLine]);

    return (
        <Collapsible defaultOpen={defaultOpen}>
            <div
                className={cn(
                    "flex flex-col overflow-hidden rounded-lg border border-border-base bg-surface-muted px-1.5 py-1.5",
                )}
            >
                <FragmentFileModificationHeader
                    filePath={fragment.filePath}
                    diff={diff}
                    onHandleReveal={onHandleReveal}
                />
                <CollapsibleContent>
                    <FragmentFileModificationDiff actionId={fragment.actionId} diff={diff} />
                </CollapsibleContent>
            </div>
        </Collapsible>
    );
};

const FragmentFileModificationHeader: FC<{
    filePath: string;
    diff?: ParsedDiff;
    onHandleReveal?: () => void;
}> = ({ filePath, diff, onHandleReveal }) => {
    const [copied, setCopied] = useTemporaryState(false, 2000);

    const handleCopyPath = useCallback(
        async (e: React.MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();
            try {
                await navigator.clipboard.writeText(filePath);
                setCopied(true);
            } catch (error) {
                setCopied(false);
                toast({
                    title: "Failed to copy file path",
                    description: formatError(error),
                });
            }
        },
        [filePath, setCopied],
    );

    return (
        <div className={"flex h-8 items-center justify-between px-2 py-0"}>
            <div className="group/path flex min-w-0 items-center gap-2">
                {onHandleReveal ? (
                    <Button
                        variant="ghost"
                        size="xs"
                        LeadingIcon={IconCode}
                        className="shrink-0 text-content-secondary"
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            onHandleReveal?.();
                        }}
                        title="Open in VS Code"
                        data-tracking-id="open-file-in-vscode"
                    />
                ) : (
                    <IconCode size="base" className="shrink-0 text-content-secondary" />
                )}
                <Text className="select-text truncate text-sm font-bold text-content-secondary">{filePath}</Text>
                <Tooltip content={copied ? "Copied!" : "Copy file path"}>
                    <Button
                        variant="ghost"
                        size="xs"
                        LeadingIcon={() =>
                            copied ? (
                                <Check className="text-content-positive" size={14} />
                            ) : (
                                <Copy size={14} />
                            )
                        }
                        className="shrink-0 opacity-0 transition-opacity group-hover/path:opacity-100"
                        onClick={handleCopyPath}
                        aria-label="Copy file path"
                        title="Copy file path"
                        data-tracking-id="copy-file-path"
                    />
                </Tooltip>
            </div>
            <CollapsibleTrigger asChild>
                <button
                    className="flex shrink-0 cursor-pointer items-center gap-1 rounded px-1 text-sm hover:bg-surface-secondary"
                    data-tracking-id="toggle-file-diff"
                >
                    {diff?.stats && (
                        <>
                            <span className="text-content-positive">+{diff.stats.additions}</span>
                            <span className="text-content-red">-{diff.stats.deletions}</span>
                        </>
                    )}
                </button>
            </CollapsibleTrigger>
        </div>
    );
};

const FragmentFileModificationDiff: FC<{
    actionId: string;
    diff: ParsedDiff;
}> = ({ actionId, diff }) => {
    const { effectiveTheme } = useTheme();

    const options = useMemo(
        () => ({
            theme: { dark: "ona-dark", light: "ona-light" } as const,
            themeType: effectiveTheme,
            diffStyle: "unified" as const,
            diffIndicators: "bars" as const,
            hunkSeparators: "simple" as const,
            expandUnchanged: false,
            disableFileHeader: true,
            overflow: "scroll" as const,
        }),
        [effectiveTheme],
    );

    const diffStyle = useMemo(
        () =>
            ({
                "--diffs-font-family": "var(--font-family-mono)",
                "--diffs-gap-block": "0px",
            }) as React.CSSProperties,
        [],
    );

    if (!diff.patch) {
        return null;
    }

    return (
        <div
            className="rounded-lg border border-border-base bg-surface-primary"
            data-testid={`fragment-diff-${actionId}`}
        >
            <ScrollArea orientation="both" className="max-h-[300px] pl-[3px]">
                <PatchDiff patch={diff.patch} options={options} style={diffStyle} />
            </ScrollArea>
        </div>
    );
};
